// @flow

import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { I18n, Translate } from 'react-redux-i18n';
import FilterBar, { Location as LocationType } from './FilterBar';
import type { GeminiRights, ShoppingCenter } from '../../../types/shoppingCenterType';
import type {
  ComparatorContent,
  CoqueRetailer,
  Extremas,
  IntermediateStoreSearchCriteria,
  SearchedBranchLevel,
  Store,
  StoreSearchCriteria,
} from '../../../types/apolloType';
import {
  getCurrentShoppingCenter,
  parseUrl,
} from '../../../utils/urlUtils';
import TuileMainRetailer from './components/TuileMainRetailer';
import TuileComparator from './components/TuileComparator';
import RippleButton from '../../../components/RippleButton';
import storesApi from '../../../network/api/storesApi';
import EllipsisLoader from '../../../components/EllipsisLoader';
import { notifyApolloError } from '../../../network/notification';
import Loader from '../../common/Loader';
import StoreDealPerformance from './components/StoreDealPerformance';
import { trackAction } from '../../../components/GoogleAnalyticsWrapper';
import { updateRetailerSelected } from '../../../state/stores/storeService';

type Props = {
  // Redux
  apiKey: string,
  baseShoppingCenterList: ShoppingCenter[],
  userGeminiRights: GeminiRights,
  dispatch: () => void,
  location: Location,
}

type State = {
  result: CoqueRetailer[],
  store: Store,
  retailersAvailable: CoqueRetailer[],
  extremas: Extremas,
  mapIsReady: boolean,
  nbOfRetailers: number,
  searchCriterias: StoreSearchCriteria,
  isLoading: boolean,
  currentRetailer: ?CoqueRetailer,
  loadingGenerationStore: number,
  branchLevel: SearchedBranchLevel,
  sbrStorePercent: number,
  idsTopRetailers: Array<number>,
  isLoadingCustomRetailer: boolean,
}

const nbOfRetailers = 5;
const SEARCH = 'SEARCH';
const DL_M30 = 'DL_M30';
const CHART = 'CHART';

class StoreData extends React.Component<Props, State> {
  state = {
    result: null,
    store: null,
    retailersAvailable: null,
    sbrStorePercent: null,
    nbOfRetailers: 10,
    extremas: null,
    searchCriterias: {
      bl1id: null,
      bl2id: null,
      bl3id: null,
      selectedBL: [],
      codstr: null,
      internationalRetailer: true,
      pcCodeList: [],
      countryCodeList: [],
      equivalentGla: false,
      location: LocationType.POOL_COUNTRY.shortName,
      insideSc: false,
      mustBeFlagship: false,
      scCategory: null,
      mustBeWestfield: false,
      su: false,
      msu: false,
      lu: false,
      allBl: false,
    },
    isLoading: true,
    loadingGenerationStore: null,
    currentRetailer: null,
    branchLevel: {
      id: null,
      label: I18n.t('comparator.sameBranchLevel2', { bl2: '' }),
      type: 'bl2',
      bl1Id: null,
    },
    idsTopRetailers: [],
    addedCustomRetailer: null,
    isLoadingCustomRetailer: false,
  };

  componentDidMount() {
    this.props.dispatch(updateRetailerSelected(null));

    const params = parseUrl(this.props.location.search);
    if (params && params.store) {
      storesApi.fetchMainStore(params.store)
        .then(resp => resp.json())
        .then((json: Store) => {
          if (json) {
            this.props.dispatch(updateRetailerSelected(json));
            this.setState({
              store: json,
            });
          }
        }).catch(e => this.props.dispatch(notifyApolloError(e)))
        .finally(() => this.setState({ isLoading: false }));
    }

    // storesApi.fetchTopRetailers()
    //   .then(response => response.json())
    //   .then(response => {
    //     this.setState({ idsTopRetailers: response })
    //   });
  }

  fetchStoresResults = (searchCriteria: StoreSearchCriteria) => {
    const params = parseUrl(this.props.location.search);
    if (params && params.store) {
      this.setState({ isLoading: true });
      trackAction(SEARCH);
      storesApi.fetchStoresResults(
        params.store,
        searchCriteria.bl1id,
        searchCriteria.bl2id,
        searchCriteria.bl3id,
        searchCriteria.selectedBL,
        searchCriteria.location,
        searchCriteria.internationalRetailer,
        searchCriteria.equivalentGla,
        searchCriteria.pcCodeList,
        searchCriteria.countryCodeList,
        searchCriteria.mustBeFlagship,
        searchCriteria.scCategory,
        searchCriteria.mustBeWestfield,
        searchCriteria.su,
        searchCriteria.msu,
        searchCriteria.lu,
        searchCriteria.allBl,
        searchCriteria.insideSc,
      ).then(resp => resp.json())
        .then((json: ComparatorContent) => {
          this.props.dispatch(updateRetailerSelected(json.store));
          this.setState({
            store: json.store,
            result: json.result,
            sbrStorePercent: json.sbrStorePercent,
            extremas: this.getExtremas(json),
            searchCriterias: {
              ...this.state.searchCriterias,
              codstr: json.store.codstr,
            },
          });
        }).catch(e => this.props.dispatch(notifyApolloError(e)))
        .finally(() => this.fetchAvailableRetailersForCodstr(params.store));
    }
  };

  fetchAvailableRetailersForCodstr = (codstr: number) => {
    storesApi.fetchAvailableRetailers(codstr)
      .then(response => response.json())
      .then(response => this.setState({ retailersAvailable: response }))
      .finally(() => this.setState({ isLoading: false }));
  };

  getExtremas = (stores: ComparatorContent) => {
    const extrema = {};
    extrema.ervMax = stores.store.erv;
    extrema.mgrMax = stores.store.mgr;
    extrema.ocrMax = stores.store.ocr;
    extrema.salesMax = stores.store.sales;
    extrema.surroundingMax = stores.store.matchingSurrounding;
    extrema.footfallMax = stores.store.footfall;
    extrema.branchLevelMixMax = stores.store.branchLevelRepresentation;
    extrema.branchLevelPerformanceMax = stores.store.branchLevelPerformance;
    extrema.storesInCatchmentMax = stores.store.storesInCatchment;
    extrema.sbrMax = stores.store.sbr;
    extrema.schStoreMax = stores.store.schStore;
    extrema.schRetailerMax = stores.store.schRetailer;

    stores.result.forEach((storeItem: CoqueRetailer) => {
      if (storeItem.projectedErv > extrema.ervMax) {
        extrema.ervMax = storeItem.projectedErv;
      }
      if (storeItem.projectedOcr < extrema.ocrMax) {
        extrema.ocrMax = storeItem.projectedOcr;
      }
      if (storeItem.projectedMgr > extrema.mgrMax) {
        extrema.mgrMax = storeItem.projectedMgr;
      }
      if (storeItem.projectedSales > extrema.salesMax) {
        extrema.salesMax = storeItem.projectedSales;
      }
      if (storeItem.surroundingMax > extrema.surroundingMax) {
        extrema.surroundingMax = storeItem.surroundingMax;
      }
      if (storeItem.footfallMax > extrema.footfallMax) {
        extrema.footfallMax = storeItem.footfallMax;
      }
      if (storeItem.branchLevelRepresentation < extrema.branchLevelMixMax) {
        extrema.branchLevelMixMax = storeItem.branchLevelRepresentation;
      }
      if (storeItem.branchLevelPerformance > extrema.branchLevelPerformanceMax) {
        extrema.branchLevelPerformanceMax = storeItem.branchLevelPerformance;
      }
      if (storeItem.storesInCatchmentMax > extrema.storesInCatchmentMax) {
        extrema.storesInCatchmentMax = storeItem.storesInCatchmentMax;
      }
      if (storeItem.projectedSbr > extrema.sbrMax) {
        extrema.sbrMax = storeItem.projectedSbr;
      }
      if (storeItem.schStore > extrema.schStoreMax) {
        extrema.schStoreMax = storeItem.schStore;
      }
      if (storeItem.schRetailer > extrema.schRetailerMax) {
        extrema.schRetailerMax = storeItem.schRetailer;
      }
    });
    return extrema;
  };

  generateM30 = (selectedRetailer) => {
    this.setState({ loadingGenerationStore: selectedRetailer.codeRetailerProposition });
    trackAction(DL_M30);
    storesApi.generateM30(
      this.state.searchCriterias,
      selectedRetailer.codeRetailerProposition,
      selectedRetailer.isCustom,
    ).then(result => result.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.target = '_blank';
        a.download = 'Commercial Operation Model.xlsx';
        a.click();
        this.setState({
          loadingGenerationStore: null,
        });
      });
  };

  deleteStore = (retailer: CoqueRetailer) => {
    const results = this.state.result
      .filter(value => value.codeRetailerProposition !== retailer.codeRetailerProposition);
    this.setState({
      result: results,
      retailersAvailable: [
        retailer,
        ...this.state.retailersAvailable,
      ],
      extremas: this.getExtremas({
        store: this.state.store,
        retailersAvailable: this.state.retailersAvailable,
        result: results,
      }),
    });
  };

  addCustomRetailer = (retailer) => {
    this.setState({ isLoadingCustomRetailer: true });
    storesApi.fetchCustomRetailerResults(retailer.codeRetailerProposition, this.state.store.codstr)
      .then(response => response.json())
      .then((responseJson) => {
        this.setState({ addedCustomRetailer: responseJson });
        const results = this.state.retailersAvailable
          .filter(value => value.codeRetailerProposition !== retailer.codeRetailerProposition);
        this.setState({
          retailersAvailable: results,
          result: [
            responseJson,
            ...this.state.result,
          ],
          extremas: this.getExtremas({
            store: this.state.store,
            retailersAvailable: this.state.retailersAvailable,
            result: [
              ...this.state.result,
              responseJson,
            ],
          }),
        });
        this.setState({ isLoadingCustomRetailer: false });
      });
  };

  viewOffer = (retailer: CoqueRetailer) => {
    trackAction(CHART);
    this.setState({ currentRetailer: retailer });
  };

  getSelectedCountriesPhrase = () => (
    (
      this.state.searchCriterias.countryCodeList
      && this.state.searchCriterias.countryCodeList.length > 0
    ) ? (
        I18n.t(
          'comparator.phrase.inTheSelectedCountries',
          { countries: this.state.searchCriterias.countryCodeList.map(country => country).join(', ') },
        )
      ) : ''
  );

  updateFilterPhrase = () => {
    if (this.state.searchCriterias
      && (
        this.state.searchCriterias.internationalRetailer
        || this.state.inComparableSC
        || this.state.branchLevel
        || this.state.location)
    ) {
      return `${I18n.t('comparator.phrase.searchingAmong')}
        ${this.state.searchCriterias.internationalRetailer
        ? I18n.t('comparator.phrase.internationalRetailers')
        : I18n.t('comparator.phrase.allRetailers')}
        ${this.state.inComparableSC ? I18n.t('comparator.phrase.inComparableSC') : ''}
        ${(this.state.branchLevel && this.state.branchLevel.label) ? I18n.t(`comparator.phrase.${this.state.searchCriterias.allBl ? 'with' : 'withThe'}`).concat(this.state.branchLevel.label) : ''}
        ${this.state.searchCriterias.selectedBL && this.state.searchCriterias.selectedBL.length > 0 ? I18n.t('comparator.phrase.inTheSelectedBls') : ''}
        ${this.state.location !== 'unconcerned' ? `${I18n.t('comparator.phrase.located')} ${I18n.t(`comparator.phrase.locationType.${this.state.searchCriterias.insideSc ? 'inside' : 'outside'}`)}` : ''}
        ${this.getSelectedCountriesPhrase()}
        ${this.state.searchCriterias.equivalentGla ? I18n.t('comparator.phrase.equivalentGla') : ''}
        ${this.state.result ? `(Results: ${this.state.result.length})` : ''}`;
    }
    return I18n.t('comparator.phrase.default');
  };

  filterBarSearch = (intermediateSearchCriterias: IntermediateStoreSearchCriteria) => {
    this.setState(
      {
        searchCriterias: {
          bl1id: intermediateSearchCriterias.bl1 ?
            intermediateSearchCriterias.bl1.id : null,
          bl2id: intermediateSearchCriterias.bl2 ?
            intermediateSearchCriterias.bl2.id : null,
          bl3id: intermediateSearchCriterias.bl3 ?
            intermediateSearchCriterias.bl3.id : null,
          selectedBL: intermediateSearchCriterias.selectedBL,
          insideSc: intermediateSearchCriterias.insideSc,
          location: intermediateSearchCriterias.location,
          internationalRetailer: intermediateSearchCriterias.internationalRetailer,
          equivalentGla: intermediateSearchCriterias.equivalentGla,
          codstr: this.state.store && this.state.store.codstr,
          pcCodeList: intermediateSearchCriterias.pcCodeList,
          countryCodeList: intermediateSearchCriterias.countryCodeList,
          mustBeFlagship: intermediateSearchCriterias.mustBeFlagship,
          scCategory: intermediateSearchCriterias.scCategory,
          mustBeWestfield: intermediateSearchCriterias.mustBeWestfield,
          allBl: intermediateSearchCriterias.allBl,
          su: intermediateSearchCriterias.su,
          msu: intermediateSearchCriterias.msu,
          lu: intermediateSearchCriterias.lu,
        },
        branchLevel: intermediateSearchCriterias.branchLevel
          ? intermediateSearchCriterias.branchLevel
          : {
            id: null,
            label: I18n.t('comparator.sameBranchLevel2', { bl2: '' }),
            type: 'bl2',
            bl1Id: null,
          },
        isLoading: true,
        currentRetailer: null,
      },
      () => this.fetchStoresResults(this.state.searchCriterias),
    );
  }

  render() {
    const shoppingCenter = getCurrentShoppingCenter(
      this.props.location.search,
      this.props.baseShoppingCenterList,
    );
    const params = parseUrl(this.props.location.search);

    if (
      !shoppingCenter
      || (this.state.isLoading && (!this.state.store || !this.props.baseShoppingCenterList))
    ) {
      return (
        <div className="scrollable-content empty-background">
          <div className="store-data">
            <Loader isLoading />
          </div>
        </div>
      );
    }
    if (!this.state.isLoading && !this.state.store) {
      return (
        <div className="scrollable-content empty-background">
          <div className="store-data">
            <div className="no-element">
              <div key="key1" className="text">
                {I18n.t('comparator.noStoreData')}
              </div>
              <i key="key2" className="fa fa-exclamation-triangle" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="scrollable-content scrollable-content--snap empty-background"
      >
        <div className="store-data">
          <FilterBar
            search={this.filterBarSearch}
            currentStore={this.state.store}
            shoppingCenter={shoppingCenter}
            retailersAvailable={this.state.retailersAvailable}
            retailersDisplayed={this.state.result}
            addCustomRetailer={this.addCustomRetailer}
            isLoading={this.state.isLoading}
            locationParams={params}
            idsTopRetailers={this.state.idsTopRetailers}
            isLoadingCustomRetailer={this.state.isLoadingCustomRetailer}
          />
          {
            this.state.store && (
              <div className="stores-container">
                <div className="filter-bar-container-external">
                  <div className="filter-bar-container">
                    <div className="filter-bar-phrase">{this.updateFilterPhrase()}</div>
                  </div>
                </div>
                <div className="retailer-comparator-container">
                  {
                    this.state.store && (
                      <div className="tile">
                        <TuileMainRetailer
                          store={this.state.store}
                          sbrStorePercent={this.state.sbrStorePercent}
                        />
                      </div>
                    )
                  }
                  <div className="scroll-tiles-container">
                    {
                      !this.state.isLoading
                      && this.state.extremas
                      && this.state.result
                      && (
                        this.state.result
                          .slice(0, this.state.nbOfRetailers)
                          .map((storeItem: CoqueRetailer, index: number) => (
                            <div
                              className="retailer-values"
                              key={storeItem.codeRetailerProposition}
                            >
                              <div
                                className={`tile${this.state.currentRetailer
                                && storeItem.codeRetailerProposition === this.state.currentRetailer.codeRetailerProposition ? ' selected' : ''}`}
                              >
                                <TuileComparator
                                  shoppingCenterList={this.props.baseShoppingCenterList}
                                  viewOffer={() => this.viewOffer(storeItem)}
                                  currentStore={storeItem}
                                  mainRetailer={this.state.store}
                                  shoppingCenter={shoppingCenter}
                                  extrema={this.state.extremas}
                                  deleteStore={this.deleteStore}
                                  searchCriterias={this.state.searchCriterias}
                                  generateM30={this.generateM30}
                                  isGenerationDisabled={this.state.loadingGenerationStore != null}
                                  isGenerationLoading={
                                    this.state.loadingGenerationStore === storeItem.codeRetailerProposition
                                  }
                                  isBp={
                                    params.bp
                                    && index === this.state.result.map(res => res.retailerName).indexOf(params.bp)
                                  }
                                  isLocomotive={
                                    this.state.idsTopRetailers.includes(storeItem.codeRetailerProposition)
                                  }
                                />
                              </div>
                            </div>
                          )))}
                    {
                      this.state.isLoading
                        ? (<EllipsisLoader />)
                        : (
                          <div className="retailer-values">
                            {
                              this.state.result
                              && (this.state.nbOfRetailers <= this.state.result.length) && (
                                <RippleButton
                                  className="more-retailers-button"
                                  onClick={() => this.setState({
                                    nbOfRetailers: (this.state.nbOfRetailers + nbOfRetailers),
                                  })}
                                >
                                  <h1>+</h1>
                                  <Translate value="comparator.moreRetailers" />
                                </RippleButton>
                              )}
                          </div>
                        )}
                  </div>
                </div>
              </div>
            )}
        </div>
        {
          this.state.currentRetailer && (
            <StoreDealPerformance
              dispatch={this.props.dispatch}
              apiKey={this.props.apiKey}
              currentStore={this.state.store}
              shoppingCenter={shoppingCenter}
              currentRetailer={this.state.currentRetailer}
              topRetailerIds={this.state.idsTopRetailers}
              addedCustomRetailer={this.state.addedCustomRetailer}
              locationParams={params}
              userGeminiRights={this.props.userGeminiRights}
            />
          )}
      </div>
    );
  }
}

export default withRouter(connect(state => ({
  apiKey: state.google.mapsApiKey,
  baseShoppingCenterList: state.shoppingCenterFilters.baseShoppingCenterList,
  userGeminiRights: state.shoppingCenterFilters.userGeminiRights,
}))(StoreData));
